import { useEffect, useRef, useState } from "react";
import {
  AlertIcon,
  GreenTick,
  ManagerTickIcon,
  RevertIcon,
  SaveTaxRateIcon,
  VerifyAndPrint,
} from "../../icons/Icon";
import Button from "../Dealers/Button";
import InputTag from "../FormComponents/InputTag";
import styles from "../../../pages/ordermanager/Orders.module.css";
import FileUpload from "./FileUpload";
import VerticalDropDown2 from "../FormComponents/VerticalDropDown2";
import { useToast } from "../Toaster/Toaster";
import { Close } from "../../icons/Icon3";
import { useAuth } from "../../context/Auth";
import { formatDateTime } from "../../../Utils/Utils";
import ModalWrapper from "../layoutWrapper/ModalWrapper";
import ConfirmVerification from "./ConfirmVerification";

const PAYMENT_METHODS = {
  CASH: "Cash",
  PHOTO: "Photo",
  POD: "POD",
  ONLINE: "Online",
};

const PAYMENT_TYPES = {
  CASH: "Cash",
  PHOTO: "Photo",
  BANK_API: "BankAPI",
};

const RECEIPT_STATUS = {
  RECEIVED: "Received",
  REFUND: "Refund",
  CANCELLED: "Cancelled",
  CASH_PENDING: "Cash Pending at Collector",
  PENDING_REFUND: "Pending For Refund",
};

const getResetFieldsForPaymentMethod = (method, currentData) => {
  const updatedData = { ...currentData, paymentMethod: method };

  switch (method) {
    case PAYMENT_METHODS.CASH:
      return {
        ...updatedData,
        accountNumber: "",
        paymentType: PAYMENT_TYPES.CASH,
        transactionId: "",
        transactionScreenshot: "",
      };
    case PAYMENT_METHODS.PHOTO:
      return { ...updatedData, paymentType: PAYMENT_TYPES.PHOTO };
    case PAYMENT_METHODS.POD:
      return {
        ...updatedData,
        accountNumber: "",
        paymentType: PAYMENT_TYPES.CASH,
        transactionId: "",
        transactionScreenshot: "",
      };
    default:
      return updatedData;
  }
};

const validateField = (field, value) => {
  const validators = {
    paymentAmount: (val) =>
      val && !isNaN(val) ? null : "Payment amount must be a valid number.",
    accountNumber: (val) => (val ? null : "Account number is required."),
    transactionScreenshot: (val) =>
      val ? null : "Transaction screenshot is required.",
    transactionId: (val) => (val ? null : "Transaction ID is required."),
  };

  return validators[field] ? validators[field](value) : null;
};

const getValidationRules = (paymentMethod, paymentType) => {
  const rules = {
    [PAYMENT_METHODS.CASH]: ["paymentAmount"],
    [PAYMENT_METHODS.PHOTO]: [
      "paymentAmount",
      "accountNumber",
      "transactionScreenshot",
      "transactionId",
    ],
    [PAYMENT_METHODS.POD]:
      paymentType === PAYMENT_TYPES.CASH
        ? ["paymentAmount"]
        : [
            "paymentAmount",
            "accountNumber",
            "transactionScreenshot",
            "transactionId",
          ],
    [PAYMENT_METHODS.ONLINE]:
      paymentType === PAYMENT_TYPES.BANK_API
        ? ["transactionId"]
        : ["transactionScreenshot", "accountNumber", "transactionId"],
  };

  return rules[paymentMethod] || [];
};

const ReceiptHeader = ({ type, mode, localReceiptData, receiptData }) => (
  <div style={{ alignItems: "flex-start" }}>
    <p style={{ color: mode ? "#fff" : "#000" }}>
      {(type === "create" || type === "edit") && "Receipt"}
      {type === "update" && "Update Receipt"}
    </p>

    {type !== "create" && (
      <div
        className={styles.receipt_dets}
        style={{ color: mode ? "#FFFFFF" : "#000000" }}
      >
        <p style={{ margin: 0 }}>
          {localReceiptData.receiptId} / {localReceiptData.orderId} /{" "}
          {localReceiptData.receiptDate}
        </p>
        <p>{receiptData?.storeName + " / " + receiptData?.city}</p>
        <p>
          {!localReceiptData.verifyBy
            ? "Not Received"
            : localReceiptData.verifyBy}
        </p>
      </div>
    )}
  </div>
);

const AccountDropdown = ({
  accounts,
  localReceiptData,
  handleFieldChange,
  isReadOnly,
}) => {
  const selectedAccount = accounts.find(
    (a) => a.accountNumber === localReceiptData.accountNumber,
  );

  const dropDownText = selectedAccount
    ? `${localReceiptData.accountNumber} / ${selectedAccount.bankName}`
    : "Select";

  return (
    <VerticalDropDown2
      height="37px"
      fontSize="12px"
      label="Account"
      dropDownText={dropDownText}
      options={accounts.map((a) => ({
        label: `${a.accountNumber} / ${a.bankName}`,
        value: a.accountNumber,
      }))}
      value={{ label: dropDownText, value: localReceiptData.accountNumber }}
      onchange={(option) => handleFieldChange("accountNumber", option.value)}
      disableOpen={isReadOnly}
      star
    />
  );
};

const TransactionInput = ({
  localReceiptData,
  handleFieldChange,
  isReadOnly,
}) => (
  <InputTag
    padding="8px"
    fontSize="12px"
    labelText="Transaction ID / RRN No."
    placeholderText="Ref / UTR Number / Transaction ID"
    placeholderType="text"
    value={localReceiptData.transactionId || ""}
    onChange={(e) => handleFieldChange("transactionId", e.target.value)}
    maxLength={200}
    required
    readOnly={isReadOnly}
    star
  />
);

const VerificationStatus = ({ mode, verifyTime, verifyDate }) => (
  <div className={styles.verified_con}>
    <span style={{ color: mode && "#FFFFFF" }}>
      *Verified & Received on: {verifyTime}, {formatDateTime(verifyDate)}
    </span>
    <div>
      <GreenTick />
    </div>
  </div>
);

const ActionButtons = ({
  localReceiptData,
  handleCloseModal,
  revertCase,
  setIsPrint,
  buttonClick,
  loading,
  showCollect,
  type,
  flag,
  designations,
}) => {
  const renderStatusButton = () => {
    const statusButtons = {
      [RECEIPT_STATUS.RECEIVED]: (
        <Button
          buttonBgColor="#4164E3"
          buttonTextColor="#fff"
          buttonIcon={<ManagerTickIcon color="#FFFFFF" />}
          buttonText="Verified"
        />
      ),
      [RECEIPT_STATUS.CASH_PENDING]: (
        <Button
          buttonBgColor="#31974D"
          buttonTextColor="#fff"
          buttonIcon={<ManagerTickIcon color="#FFFFFF" />}
          buttonText="Collected"
        />
      ),
      [RECEIPT_STATUS.REFUND]: (
        <Button
          buttonBgColor="#31974D"
          buttonTextColor="#fff"
          buttonIcon={<ManagerTickIcon color="#FFFFFF" />}
          buttonText="Refunded"
        />
      ),
      [RECEIPT_STATUS.PENDING_REFUND]: (
        <Button
          buttonBgColor="#F56221"
          buttonTextColor="#fff"
          buttonIcon={<AlertIcon />}
          buttonText="Refund Pending"
        />
      ),
      [RECEIPT_STATUS.CANCELLED]: (
        <Button
          buttonBgColor="#E93445"
          buttonTextColor="#fff"
          buttonIcon={<ManagerTickIcon color="#FFFFFF" />}
          buttonText="Cancelled"
        />
      ),
    };

    return statusButtons[localReceiptData.status] || null;
  };

  const statusButton = renderStatusButton();
  const showRevertButton =
    localReceiptData.status !== RECEIPT_STATUS.RECEIVED &&
    localReceiptData.status !== RECEIPT_STATUS.CANCELLED &&
    localReceiptData.paymentType === PAYMENT_TYPES.PHOTO &&
    localReceiptData.paymentMethod === PAYMENT_METHODS.ONLINE;

  const getActionButtonText = () => {
    if (showCollect) return "Collect";
    if (type === "create") return "SAVE";
    if (type === "update") return "Verify & Update";
    return "Verify";
  };

  return (
    <div style={{ gap: "1rem", justifyContent: "center", marginTop: "0.5rem" }}>
      <Button
        buttonBgColor="#000"
        buttonText="Close"
        buttonTextColor="#fff"
        buttonIcon={<Close />}
        buttonClick={handleCloseModal}
      />

      {showRevertButton && (
        <Button
          buttonBgColor="#F93E3E"
          buttonText="Revert Back"
          buttonTextColor="#FFFFFF"
          buttonIcon={<RevertIcon color="#FFFFFF" />}
          buttonClick={() => {
            handleCloseModal();
            revertCase();
          }}
        />
      )}

      {statusButton || (
        <>
          <Button
            buttonBgColor="#4164E3"
            buttonText={getActionButtonText()}
            buttonTextColor="#fff"
            buttonIcon={<SaveTaxRateIcon />}
            buttonClick={() => {
              buttonClick();
              setIsPrint(false);
            }}
            disabled={loading}
            loading={loading}
          />

          {(flag === "verify" || type === "update") &&
            designations.includes("superadmin") && (
              <Button
                buttonBgColor="#31974D"
                buttonText="Verfiy & Print"
                buttonTextColor="#FFFFFF"
                buttonIcon={<VerifyAndPrint />}
                buttonClick={() => {
                  buttonClick();
                  setIsPrint(true);
                }}
                disabled={loading}
                loading={loading}
              />
            )}
        </>
      )}
    </div>
  );
};

const AddReceiptModal = ({
  handleCloseModal,
  mode,
  totalAmount,
  accounts = [],
  receiptData,
  updateReceiptData,
  type = "create",
  revertCase,
  flag,
  isPodBlocked,
  showCollect,
  receiptTotalAmount,
  isSelf,
  orderType,
}) => {
  // console.log("showCollect ", showCollect);
  const { show } = useToast();
  const { designations } = useAuth();
  const transactionAmountRef = useRef(null);

  const [localReceiptData, setLocalReceiptData] = useState(receiptData);
  const [loading, setLoading] = useState(false);
  const [isPOD, setIsPOD] = useState(false);
  const [payAmount, setPayAmount] = useState();
  const [openModal, setOpenModal] = useState(false);
  const [isPrint, setIsPrint] = useState(false);
  const [image, setImage] = useState(
    receiptData?.transactionScreenshot || null,
  );
  const [modalName, setModalName] = useState("AddReceiptModal");

  // useEffect(() => {
  //   setModalName("AddReceiptModal");
  // }, []);

  useEffect(() => {
    if (flag === "notupdateamount") {
      setPayAmount(receiptData.paymentAmount);
    }
  }, [flag, receiptData.paymentAmount]);

  useEffect(() => {
    if (localReceiptData.paymentMethod === PAYMENT_METHODS.POD) {
      setIsPOD(true);

      if (type === "create") {
        setLocalReceiptData((prevState) => ({
          ...prevState,
          paymentAmount: totalAmount - receiptTotalAmount,
        }));
      }
    }

    if (
      type === "create" &&
      (localReceiptData.paymentMethod === PAYMENT_METHODS.CASH ||
        localReceiptData.paymentMethod === PAYMENT_METHODS.PHOTO)
    ) {
      setIsPOD(false);
    }
  }, [localReceiptData.paymentMethod, receiptTotalAmount, totalAmount, type]);

  const resetFieldsOnPaymentMethodChange = (newPaymentMethod) => {
    const updatedData = getResetFieldsForPaymentMethod(
      newPaymentMethod,
      localReceiptData,
    );
    setLocalReceiptData(updatedData);
  };

  const validateData = () => {
    const errors = {};
    const requiredFields = getValidationRules(
      localReceiptData.paymentMethod,
      localReceiptData.paymentType,
    );

    requiredFields.forEach((field) => {
      const error = validateField(field, localReceiptData[field]);
      if (error) errors[field] = error;
    });

    if (localReceiptData.paymentAmount > totalAmount) {
      show("Amount greater than total amount!", "warning");
      return false;
    }

    if (Object.keys(errors).length > 0) {
      show("Please fill all the required fields!", "warning");
      return false;
    }

    return true;
  };

  // console.log("isPrint", isPrint);

  const receiptAction = async (isPrint) => {
    setLoading(true);
    try {
      await updateReceiptData(localReceiptData, isPrint);
    } catch (error) {
      console.error("Error updating receipt:", error);
    } finally {
      setLoading(false);
    }
  };

  const buttonClick = async () => {
    if (validateData()) {
      if (type === "update" || flag === "verify") {
        setOpenModal(true);
        setModalName("ConfirmVerification");
      } else {
        await receiptAction();
      }
    }
  };

  const handleFileChange = (file) => {
    setImage(file);
    setLocalReceiptData((prevState) => ({
      ...prevState,
      transactionScreenshot: file || "",
    }));
  };

  const handleFieldChange = (field, value) => {
    setLocalReceiptData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const disableMethodSelection =
    ((flag === "notupdateamount" || flag === "verify") && isPOD) ||
    localReceiptData.status === RECEIPT_STATUS.CASH_PENDING;

  const isReadOnly = () =>
    [
      RECEIPT_STATUS.RECEIVED,
      RECEIPT_STATUS.REFUND,
      RECEIPT_STATUS.CANCELLED,
    ].includes(localReceiptData.status) ||
    (localReceiptData.paymentMethod === PAYMENT_METHODS.ONLINE &&
      localReceiptData.paymentType !== PAYMENT_TYPES.PHOTO);

  // --------------------------------------------------------------

  // const methodOptions = [
  //   { label: "Cash", value: PAYMENT_METHODS.CASH },
  //   { label: "Photo", value: PAYMENT_METHODS.PHOTO },
  //   ...(isPodBlocked || isSelf
  //     ? []
  //     : [{ label: "Pay on Delivery", value: PAYMENT_METHODS.POD }]),
  // ];

  const methodOptions = [
    { label: "Cash", value: PAYMENT_METHODS.CASH },
    { label: "Photo", value: PAYMENT_METHODS.PHOTO },
    ...(isPodBlocked || isSelf || orderType === "Production & Sale"
      ? []
      : [{ label: "Pay on Delivery", value: PAYMENT_METHODS.POD }]),
  ];

  // --------------------------------------------------------------

  const subMethodOptions = [
    { label: "Cash", value: PAYMENT_TYPES.CASH },
    { label: "Photo", value: PAYMENT_TYPES.PHOTO },
  ];

  return (
    <div
      className={styles.add_reciept_modal}
      style={mode ? { backgroundColor: "#232529" } : {}}
    >
      <ModalWrapper
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        noBtn
      >
        <ConfirmVerification
          mode={mode}
          handleCloseModal={() => setOpenModal(false)}
          onVerified={() => receiptAction(isPrint)}
          modalName={modalName}
          setModalName={setModalName}
        />
      </ModalWrapper>

      <ReceiptHeader
        type={type}
        mode={mode}
        localReceiptData={localReceiptData}
        receiptData={receiptData}
      />

      <div style={{ gap: "1rem" }}>
        <div style={{ width: "50%" }}>
          <VerticalDropDown2
            height="37px"
            fontSize="12px"
            label="Method"
            dropDownText={localReceiptData.paymentMethod || "Select Method"}
            dropDownOpen={
              type !== "create" || flag === "notupdateamount" ? false : true
            }
            options={methodOptions}
            value={methodOptions.find(
              (option) => option.value === localReceiptData.paymentMethod,
            )}
            onchange={(option) => {
              resetFieldsOnPaymentMethodChange(option.value);
              transactionAmountRef.current?.focus();
            }}
            disableOpen={
              isReadOnly() ||
              disableMethodSelection ||
              showCollect ||
              localReceiptData.paymentMethod === PAYMENT_METHODS.ONLINE
            }
            star
          />
        </div>

        <div style={{ width: "50%" }}>
          <InputTag
            padding="8px"
            fontSize="12px"
            labelText={flag === "verify" ? "Receipt Amount" : "Total Amount"}
            placeholderText="000.00"
            value={
              type !== "create" && flag !== "verify"
                ? localReceiptData.totalAmount
                : totalAmount
            }
            readOnly
          />
        </div>
      </div>

      {localReceiptData.paymentMethod === PAYMENT_METHODS.ONLINE && (
        <>
          <InputTag
            padding="8px"
            fontSize="12px"
            labelText="Payment Type"
            placeholderText="000.00"
            value={localReceiptData.paymentType}
            readOnly
          />
          {localReceiptData.paymentType === PAYMENT_TYPES.PHOTO && (
            <>
              <FileUpload
                mode={mode}
                image={image}
                setImage={setImage}
                onFileChange={handleFileChange}
                viewOnly={isReadOnly()}
              />
              <AccountDropdown
                accounts={accounts}
                localReceiptData={localReceiptData}
                handleFieldChange={handleFieldChange}
                isReadOnly={isReadOnly()}
              />
            </>
          )}
          <TransactionInput
            localReceiptData={localReceiptData}
            handleFieldChange={handleFieldChange}
            isReadOnly={isReadOnly()}
          />
        </>
      )}

      {localReceiptData.paymentMethod === PAYMENT_METHODS.PHOTO && (
        <>
          <AccountDropdown
            accounts={accounts}
            localReceiptData={localReceiptData}
            handleFieldChange={handleFieldChange}
            isReadOnly={isReadOnly()}
          />
          <FileUpload
            mode={mode}
            image={image}
            setImage={setImage}
            onFileChange={handleFileChange}
            viewOnly={isReadOnly()}
          />
          <TransactionInput
            localReceiptData={localReceiptData}
            handleFieldChange={handleFieldChange}
            isReadOnly={isReadOnly()}
          />
        </>
      )}

      {localReceiptData.paymentMethod === PAYMENT_METHODS.POD && (
        <>
          <VerticalDropDown2
            height="37px"
            fontSize="12px"
            label="Sub Method"
            dropDownText={localReceiptData.paymentType || "Select"}
            options={subMethodOptions}
            value={subMethodOptions.find(
              (option) => option.value === localReceiptData.paymentType,
            )}
            onchange={(option) =>
              handleFieldChange("paymentType", option.value)
            }
            disableOpen={isReadOnly() || showCollect}
            star
          />

          {localReceiptData.paymentType === PAYMENT_TYPES.PHOTO && (
            <>
              <AccountDropdown
                accounts={accounts}
                localReceiptData={localReceiptData}
                handleFieldChange={handleFieldChange}
                isReadOnly={isReadOnly()}
              />
              <FileUpload
                mode={mode}
                image={image}
                setImage={setImage}
                onFileChange={handleFileChange}
              />
              <TransactionInput
                localReceiptData={localReceiptData}
                handleFieldChange={handleFieldChange}
                isReadOnly={isReadOnly()}
              />
            </>
          )}
        </>
      )}

      <div style={{ display: "flex", flexDirection: "column" }}>
        <InputTag
          padding="8px"
          fontSize="12px"
          labelText={type === "create" ? "Transaction" : "Received Amount"}
          placeholderText="Enter Transaction Amount (In INR)"
          placeholderType="number"
          ref={transactionAmountRef}
          value={
            flag === "notupdateamount" && isPOD
              ? payAmount
              : localReceiptData.paymentAmount
          }
          onChange={(e) => handleFieldChange("paymentAmount", e.target.value)}
          maxLength={10}
          required
          onKeyDown={(e) => {
            if (e.key === "Enter" && type === "create") {
              buttonClick();
            }
          }}
          readOnly={
            isReadOnly() ||
            (isPOD && type === "create") ||
            flag === "notupdateamount"
          }
          star
        />
        {localReceiptData.status === RECEIPT_STATUS.RECEIVED && (
          <VerificationStatus
            mode={mode}
            verifyTime={localReceiptData.verifyTime}
            verifyDate={localReceiptData.verifyDate}
          />
        )}
      </div>

      <ActionButtons
        localReceiptData={localReceiptData}
        handleCloseModal={handleCloseModal}
        revertCase={revertCase}
        buttonClick={buttonClick}
        setIsPrint={setIsPrint}
        loading={loading}
        showCollect={showCollect}
        type={type}
        flag={flag}
        designations={designations}
      />
    </div>
  );
};

export default AddReceiptModal;
